import React, { useState, useEffect } from "react";
import Sidebar from "../../../shared/components/layout/Sidebar";
import Tabs from "../components/Tabs";
import MarketingSection from "../components/MarketingSection";

import Settings from "../../settings/pages/Settings";
import UserSettings from "../../settings/pages/UserSettings";
import { Menu, Home, BarChart } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import IntegrationManager from './IntegrationManager'
import History from "./History.js";
import ActivityPage from '../../conversion-events/pages/Activity'
import UserDetailPage from '../../conversion-events/pages/UserDetailPage'
import GroupDetailPage from '../../conversion-events/pages/GroupDetailPage'
import Events from '../../conversion-events/pages/Events'
import HeatmapPage from '../../conversion-events/pages/HeatmapPage'
import CustomEvents from './CustomEvents'
import Campaigns from './Campaigns'

import CQIntelligence from '../../intelligence/pages/CQIntelligence'
import Cipher from './Cipher'
import SubscriptionRequired from "../../../shared/components/subscription/SubscriptionRequired";
import { useSubscription } from "../../../shared/context/subscriptionContext";
import { PinnedBoardsProvider } from "../../analytics/mixpanel/context/PinnedBoardsContext";
import axiosInstance from '../../../shared/services/api/axiosInstance';
import sdkApi from '../../../shared/utils/sdkApi';
// ContractDataContext removed - onchain functionality removed

const Dashboard = () => {
  const params = useParams();
  const teamIdFromUrl = params.team;
  // State management
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState("dashboard");
  const [isPageLoading, setIsPageLoading] = useState(true); // Track if page is being determined from URL
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTeam, setSelectedTeam] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem("selectedTeam") || "";
    }
    return "";
  });
  const [isLoading, setIsLoading] = useState(true);
  const [websiteData, setWebsiteData] = useState({
    totalWebsites: 0,
    recentVisitors: 0,
    activeWebsites: 0
  });
  
  // ContractDataContext removed - onchain functionality removed
  // const { contractArray, isLoadingContracts, refreshContracts } = useContractData();
  
  // Simple Project Nova Design System styles
  const styles = {
    primaryColor: "#1d0c46", // Deep Indigo
    secondaryColor: "#caa968", // Muted Gold
    backgroundColor: "#f8fafc", // Light gray background
    textColor: "#0f172a",
    textHeaders: "#1d0c46", // Deep Indigo for headers
    borderColor: "rgba(255, 255, 255, 0.18)", // Glass border
    glassEffect: "rgba(255, 255, 255, 0.7)",
    glassBorder: "rgba(255, 255, 255, 0.18)",
    cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)"
  };
  
  // Get subscription status at component level
  const { isActive: hasSubscription, plan, status } = useSubscription();

  // Screen size detection with multiple breakpoints
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024
      });
      
      if (width >= 1024 && !isSidebarOpen && selectedPage !== 'settings') {
        setIsSidebarOpen(true);
      } else if (width < 640 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    
    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, [isSidebarOpen, selectedPage]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // refreshContracts removed - onchain functionality removed
        
        // Fetch websites for this team
        const teamId = selectedTeam;
        if (!teamId) {
          setIsLoading(false);
          return;
        }
        
        // Check sessionStorage cache first
        const cacheKey = `websites_${teamId}`;
        const cached = sessionStorage.getItem(cacheKey);
        let websites = null;
        
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            // Use cache if less than 5 minutes old
            if (Date.now() - timestamp < 5 * 60 * 1000) {
              websites = data;
            }
          } catch (e) {
            // Failed to parse cached websites
          }
        }
        
        // Fetch from API if no cache available
        if (!websites) {
          const response = await axiosInstance.get(`/website/team/${teamId}`);
          
          if (response.data && response.data.websites) {
            websites = response.data.websites;
            
            // Cache the result
            sessionStorage.setItem(cacheKey, JSON.stringify({
              data: websites,
              timestamp: Date.now()
            }));
          }
        }
        
        if (websites) {
          const activeWebs = websites.filter(site => site.isActive).length;
          
          // Get sample analytics data for the first website if available
          let visitorCount = 0;
          
          if (websites.length > 0) {
            const firstWebsite = websites[0];
            
            try {
              // Fetch analytics data
              const analyticsResponse = await sdkApi.getAnalytics(firstWebsite.siteId);
              
              // Handle different response types
              if (analyticsResponse && analyticsResponse.error) {
                // Handle timeout or server errors gracefully
                visitorCount = 0; // Default to 0 on error
              } else if (analyticsResponse && analyticsResponse.analytics) {
                visitorCount = analyticsResponse.analytics.uniqueVisitors || 0;
              }
            } catch (err) {
              console.error("Error fetching analytics:", err);
              visitorCount = 0; // Default to 0 on error
            }
          }
          
          setWebsiteData({
            totalWebsites: websites.length,
            recentVisitors: visitorCount,
            activeWebsites: activeWebs
          });
        }
      } catch (error) {
        console.error("Error fetching website data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedTeam]); // Need selectedTeam so effect runs when team loads; cache prevents excessive re-fetching

  // Sync selectedPage with URL
  useEffect(() => {
    setIsPageLoading(true); // Start loading when URL changes
    
    const path = location.pathname;
    const pathSegments = path.split('/').filter(Boolean);
    
    // Handle nested routes properly
    let currentRoute = '';
    let subRoute = '';
    if (pathSegments.length > 1) {
      currentRoute = pathSegments[1];
    }
    if (pathSegments.length > 2) {
      subRoute = pathSegments[2];
    }
    
    // Special handling for activity sub-routes
    if (currentRoute === 'activity' && subRoute === 'events') {
      setSelectedPage('activity-events');
    } else if (currentRoute === 'activity' && subRoute === 'heatmap') {
      setSelectedPage('activity-heatmap');
    } else {
      const routeToPageMap = {
        'dashboard': 'dashboard',
        'custom-events': 'custom-events',
        'campaigns': 'campaigns',
        'activity': 'activity',
        'events': 'activity',
        // 'user-journey': 'history', // User Profiles page disabled
        'managewebsites': 'managewebsites',
        'integrations': 'integrations',
        'query-studio': 'query-studio',
        'cq-intelligence': 'cq-intelligence',
        'cipher': 'cipher',
        'settings': 'settings',
        'user-settings': 'user-settings',
        'add-smart-contract': 'add-smart-contract'
      };
      
      const newPage = routeToPageMap[currentRoute] || 'dashboard';
      setSelectedPage(newPage);
      // When entering settings, collapse primary sidebar so secondary nav has room
      if (newPage === 'settings') {
        setIsSidebarOpen(false);
      }
    }
    
    // Small delay to prevent flash, then set loading to false
    setTimeout(() => {
      setIsPageLoading(false);
    }, 100);
  }, [location]);

  const handleNavigation = (page) => {
    if (page === "offchain") {
      navigate(`/${selectedTeam}/overview?primary=offchain`);
      return;
    }

    if (page === "onchain") {
      navigate(`/${selectedTeam}/overview?primary=onchain`);
      return;
    }

    setIsPageLoading(true); // Show loading when navigating
    setSelectedPage(page);
    navigate(`/${selectedTeam}/${page}`);
  };

  // Simple dashboard cards
  const dashboardCards = [
    {
      title: "Off-chain Analytics",
      description: "Track website visitors, traffic sources, and user behavior",
      primary: "offchain",
      requiredPage: "overview"
    },
    {
      title: "On-chain Explorer",
      description: "Monitor blockchain activity and transactions",
      primary: "onchain",
      requiredPage: "overview"
    },
    {
      title: "Activity",
      description: "Track and monitor detailed user and wallet behavior",
      path: "activity",
      requiredPage: "activity"
    },
    {
      title: "User Journey",
      description: "Unify your users journey across sessions and wallets",
      path: "user-journey",
      requiredPage: "history"
    },
    {
      title: "Custom Events",
      description: "Track every single interaction from clicks to form submission here",
      path: "custom-events",
      requiredPage: "custom-events"
    },
    {
      title: "Campaigns",
      description: "Measure what sources actually work for you",
      path: "campaigns",
      requiredPage: "campaigns"
    },
    {
      title: "CQ Intelligence",
      description: "Get intelligent insights from your data with AI-powered analytics",
      path: "cq-intelligence",
      requiredPage: "cq-intelligence",
      comingSoon: true
    },
    {
      title: "Custom Dashboard",
      description: "Monitor all you want here with simple querys and comprehensive dashboards",
      path: "custom-dashboard",
      requiredPage: "custom-dashboard"
    },
    {
      title: "Integration",
      description: "Connect your website and add your contract to start your journey to success",
      path: "managewebsites",
      requiredPage: "managewebsites"
    },
    {
      title: "Settings",
      description: "Manage your billings, teams and usage all in one place",
      path: "settings",
      requiredPage: "settings"
    }
  ];

  const handleCardClick = (card) => {
    if (!card || card.comingSoon) return;

    if (card.external && card.path) {
      window.open(card.path, '_blank');
      return;
    }

    if (card.primary) {
      navigate(`/${selectedTeam}/overview?primary=${card.primary}`);
      return;
    }

    if (card.path) {
      navigate(`/${selectedTeam}/${card.path}`);
    }
  };

  // Get sidebar classes based on screen size and state
  const getSidebarClasses = () => {
    if (selectedPage === "settings" && screenSize.isDesktop) {
      return "h-screen sticky top-0 flex-shrink-0 visible";
    }
    return "h-screen sticky top-0 flex-shrink-0";
  };

  // Get main content classes - optimized for performance
  const getMainContentClasses = () => {
    let baseClasses = "flex-1 flex flex-col overflow-y-auto relative transition-transform duration-200 ease-out ";
    if (screenSize.isDesktop) {
        baseClasses += isSidebarOpen ? "ml-0" : "ml-0";
    }
    return baseClasses;
  };

  // Get main padding classes
  const getMainPaddingClasses = () => {
    let paddingClasses = "p-4 md:p-6 lg:p-8 flex flex-col gap-4 md:gap-6 lg:gap-8 ";
    if (screenSize.isMobile) {
      paddingClasses += "pt-16 pb-20";
    }
    return paddingClasses;
  };

  // Render component for current page
  const renderCurrentPage = () => {
    const commonProps = {
      onMenuClick: selectedPage === "settings" ? 
        () => {} : 
        () => setIsSidebarOpen(!isSidebarOpen),
      onClose: () => setSelectedPage("dashboard"),
      screenSize: screenSize,
      selectedPage: selectedPage,
      isSidebarOpen: isSidebarOpen
    };

    const withSubscriptionCheck = (component, featureName, requiredPage = null) => {
      // Always allow dashboard, settings, and user-settings pages without subscription check
      if (selectedPage === 'dashboard' || selectedPage === 'settings' || selectedPage === 'user-settings') {
        return component;
      }
      
      // For premium pages, wrap with SubscriptionRequired using centralized access control
      return <SubscriptionRequired featureName={featureName} requiredPage={requiredPage}>{component}</SubscriptionRequired>;
    };

    switch (selectedPage) {
      case "dashboard":
        return (
          <>
            <main 
              className={getMainPaddingClasses()} 
              style={{ background: 'transparent', fontFamily: 'Raleway, sans-serif' }}
            >
              <MarketingSection />
              <Tabs isSidebarOpen={isSidebarOpen} />

              {/* Simple Dashboard Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 relative z-10">
                {/* Loading Skeleton */}
                {isLoading ? (
                  [...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white border border-gray-200 p-4 animate-pulse h-[120px]">
                      <div className="h-4 bg-gray-200 mb-2 w-2/3"></div>
                      <div className="h-3 bg-gray-200 w-full"></div>
                    </div>
                  ))
                ) : (
                  /* Simple Cards */
                  dashboardCards.map((card, index) => (
                    <div
                      key={index}
                      onClick={() => handleCardClick(card)}
                      className={`bg-white border border-gray-200 p-4 transition-colors duration-200 h-[110px] flex flex-col justify-between relative z-10 ${
                        card.comingSoon ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 
                          className="font-semibold text-[15px]"
                          style={{ 
                            fontFamily: 'Raleway, sans-serif',
                            color: styles.textHeaders
                          }}
                        >
                          {card.title}
                        </h3>
                      </div>
                      <div className="flex-1 flex flex-col justify-end">
                        <p 
                          className="text-sm leading-snug"
                          style={{ 
                            color: styles.textColor,
                            fontFamily: 'Raleway, sans-serif'
                          }}
                        >
                          {card.description}
                        </p>
                        {card.comingSoon && (
                          <span
                            className="inline-block mt-2 text-xs text-[#475569] uppercase tracking-wide"
                            style={{ fontFamily: 'Raleway, sans-serif' }}
                          >
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </main>
          </>
        );
      case "custom-events":
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return withSubscriptionCheck(
          <CustomEvents {...commonProps} />,
          "custom event tracking",
          "custom-events"
        );
      case "campaigns":
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return withSubscriptionCheck(
          <Campaigns {...commonProps} />,
          "campaign management",
          "campaigns"
        );
      case "activity":
        if (params.groupKey && params.groupId) {
          return withSubscriptionCheck(
            <GroupDetailPage {...commonProps} />,
            "activity analytics",
            "activity"
          );
        }
        if (params.distinctId) {
          return withSubscriptionCheck(
            <UserDetailPage {...commonProps} />,
            "activity analytics",
            "activity"
          );
        }
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {/* Import and use skeleton loader instead of spinner */}
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return withSubscriptionCheck(
          <ActivityPage {...commonProps} />,
          "activity analytics",
          "activity"
        );
      case "activity-events":
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return withSubscriptionCheck(
          <Events {...commonProps} />,
          "activity analytics",
          "activity"
        );

      case "activity-heatmap":
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return withSubscriptionCheck(
          <HeatmapPage {...commonProps} />,
          "heatmap analytics",
          "activity"
        );

      case "history":
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {/* Import and use skeleton loader instead of spinner */}
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return withSubscriptionCheck(
          <History {...commonProps} />,
          "historical data",
          "history"
        );
      case "managewebsites":
        return withSubscriptionCheck(
          <IntegrationManager {...commonProps} mode="websites-only" />,
          "website management",
          "managewebsites"
        );
      case "add-smart-contract":
        return withSubscriptionCheck(
          <main className={getMainPaddingClasses()}>
            <IntegrationManager {...commonProps} mode="smart-contracts-only" />
          </main>,
          "smart contracts",
          "managewebsites"
        );
      case "integrations":
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return <IntegrationManager {...commonProps} />;
      case "cq-intelligence":
        return withSubscriptionCheck(
          <CQIntelligence {...commonProps} />,
          "advanced intelligence features",
          "cq-intelligence"
        );
      case "cipher":
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return (
          <main className={getMainPaddingClasses()}>
            <Cipher {...commonProps} />
          </main>
        );
      case "settings":
        return <Settings />;
      case "user-settings":
        return (
          <main className={getMainPaddingClasses()}>
            <UserSettings />
          </main>
        );
      default:
        // Show loading spinner if page is still being determined, otherwise show 404
        if (isPageLoading) {
          return (
            <>
              <main className={getMainPaddingClasses()}>
                {/* Import and use skeleton loader instead of spinner */}
                {(() => {
                  const { ChartSkeleton } = require('../../../shared/components/ui/SkeletonLoaders');
                  return <ChartSkeleton height="400px" />;
                })()}
              </main>
            </>
          );
        }
        return (
          <>
            <main className={getMainPaddingClasses()}>
              <div className="text-center py-12">
                <h2 className="text-2xl font-semibold text-[#475569]">Page Not Found</h2>
                <p className="mt-2 text-[#475569]">The page you're looking for doesn't exist.</p>
              </div>
            </main>
          </>
        );
    }
  };

  const teamId = teamIdFromUrl || selectedTeam;

  return (
    <PinnedBoardsProvider teamId={teamId || ""}>
    <div className="flex h-screen overflow-hidden space-x-0 relative" style={{ fontFamily: 'Raleway, sans-serif' }}>
      
      <div className={getSidebarClasses()}>
        <Sidebar 
          isOpen={isSidebarOpen} 
          onClose={() => setIsSidebarOpen(false)} 
          onNavigate={handleNavigation}
          currentPage={selectedPage}
          isCompact={true} // Always compact for hover-only behavior
          screenSize={screenSize}
        />
      </div>

      <div className={`${getMainContentClasses()} relative z-10 bg-transparent`}>
        {/* Mobile menu toggle button */}
        {(screenSize.isMobile || screenSize.isTablet) && (
          <button 
            className="fixed top-4 left-4 p-2 glass-effect rounded-[12px] shadow-md focus:outline-none z-30 transition-all duration-200 hover:scale-105"
            style={{ color: styles.textColor }}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle navigation menu"
          >
            <Menu size={screenSize.isMobile ? 20 : 24} />
          </button>
        )}
        


        {renderCurrentPage()}
      </div>

      {(screenSize.isMobile || screenSize.isTablet) && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {screenSize.isMobile && (
        <div 
          className="fixed bottom-0 left-0 right-0 glass-effect border-t flex justify-around items-center py-2 z-30"
          style={{ borderColor: styles.borderColor }}
        >
          <button 
            className={`p-2 rounded-full flex flex-col items-center transition-all duration-200`}
            onClick={() => handleNavigation("dashboard")}
            aria-label="Dashboard"
            style={{ 
              color: selectedPage === "dashboard" ? styles.primaryColor : styles.textColor,
              fontFamily: 'Raleway, sans-serif'
            }}
          >
            <Home size={20} />
            <span className="text-xs mt-1">Home</span>
          </button>
          <button 
            className={`p-2 rounded-full flex flex-col items-center transition-all duration-200`}
            onClick={() => handleNavigation("offchain")}
            aria-label="Analytics"
            style={{ 
              color: styles.textColor,
              fontFamily: 'Raleway, sans-serif'
            }}
          >
            <BarChart size={20} />
            <span className="text-xs mt-1">Analytics</span>
          </button>
        </div>
      )}
    </div>
    </PinnedBoardsProvider>
  );
};

export default Dashboard;